import { FormHelperText, Box, Typography, Autocomplete, TextField } from "@mui/material";
import { FormikContextType, getIn, useFormikContext } from "formik";
import "./SelectOption.css";
import "../../App.css";

interface AutoCompleteSelectProps {
    formik?: FormikContextType<unknown>;
    formikValue: string;
    optionsList: Array<string | undefined>;
    title?: string;
    titleVariant?: "h1" | "h2" | "h3" | "h4" | "body1" | "body2" | "caption";
    description?: string;
    descriptionVariant?: "h1" | "h2" | "h3" | "h4" | "body1" | "body2" | "caption";
    inputLabel?: string;
    selectClass?: string;
    errorPath?: string;
    dataTestId?: string;
    touchedField?: string;
    disabled?: boolean;
}

const AutoCompleteSelectOption = ({
    formik,
    formikValue,
    optionsList,
    title,
    titleVariant,
    description,
    descriptionVariant,
    inputLabel,
    selectClass,
    errorPath,
    dataTestId = "auto-complete-select-component",
    touchedField,
    disabled = false,
}: AutoCompleteSelectProps): JSX.Element => {
    const formikContext = useFormikContext();
    if (!formik) formik = formikContext;

    const onChange = (primaryFormikField: string, primaryUpdateValue: unknown) => {
        formik.setFieldValue(primaryFormikField, primaryUpdateValue);
    };

    return (
        <Box className={selectClass}>
            {title && (
                <Typography variant={titleVariant} component='div' gutterBottom>
                    {title}
                </Typography>
            )}
            {description && (
                <Typography variant={descriptionVariant} component='div' sx={{ marginBottom: 2 }}>
                    {description}
                </Typography>
            )}
            <Box sx={{ minWidth: 120 }}>
                <Autocomplete
                    options={optionsList || []}
                    value={getIn(formik.values, formikValue) || ""}
                    onChange={(_, newValue) => onChange(formikValue, newValue)}
                    disabled={disabled}
                    className='input-text'
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={inputLabel}
                            error={Boolean(
                                getIn(formik.touched, touchedField || errorPath || formikValue) &&
                                    getIn(formik.errors, errorPath ? errorPath : formikValue)
                            )}
                        />
                    )}
                />
                <FormHelperText data-testid={`${dataTestId}-error`} error={true}>
                    {getIn(formik.touched, touchedField || errorPath || formikValue) &&
                        getIn(formik.errors, errorPath ? errorPath : formikValue)}
                </FormHelperText>
            </Box>
        </Box>
    );
}

export { AutoCompleteSelectOption };
